import React from 'react';
import PropTypes from 'prop-types';
import MDReactComponent from 'markdown-react-js';
import BrowseInstagram from 'components/Shared/BrowseInstagram';
import {
  getYoutubeVideoData,
  getInstagramEmbedSrc,
} from 'helpers/portfolioEmbedUrls';
import { toSeoUrl, toPlural } from 'helpers/HelperFunctions';
import { skillRoleObject } from 'helpers/Constants';

/**
 * Markdown + embed pipeline used on the new browse user cards (text projects).
 * Keeps portfolio-oriented iframe embed behavior out of `Shared/Markdown`.
 */
const BrowseMarkdown = ({
  convertLinksToEmbeds,
  suggestedRoles,
  suggestedSkills,
  ...rest
}) => {
  function domain(url) {
    return url.replace('http://', '').replace('https://', '').split('/')[0];
  }

  function preprocessHashtags(text) {
    if (!text || typeof text !== 'string') return text;

    return text.replace(
      /(?:^|\s)(#[a-zA-Z0-9_]+)(?=\s|$)/g,
      (match, hashtag) => {
        const hashtagUrl = parseHashtagUrl(hashtag);
        return hashtagUrl
          ? match.replace(hashtag, `[${hashtag}](${hashtagUrl})`)
          : match;
      }
    );
  }

  function parseHashtagUrl(hashtag) {
    const hashtagText = hashtag.replace('#', '');

    if (suggestedRoles && suggestedRoles.length > 0) {
      for (const role of suggestedRoles) {
        if (
          typeof role !== 'undefined' &&
          (role.slug.replace('-', '') === hashtagText ||
            role.slug_plural.replace('-', '') === hashtagText)
        ) {
          return '/find/' + role.slug_plural;
        }
      }
    }

    if (suggestedSkills && suggestedSkills.length > 0) {
      for (const skill of suggestedSkills) {
        if (
          typeof skill !== 'undefined' &&
          (skill.slug.replace('-', '') === hashtagText ||
            skill.slug_plural.replace('-', '') === hashtagText)
        ) {
          const role = toSeoUrl(toPlural(skillRoleObject[skill.name]));
          if (role) {
            return `/find/${role}/with/${skill.slug}`;
          }
          return `/browse?page=1&skill=${encodeURIComponent(skill.name)}`;
        }
      }
    }

    return null;
  }

  function handleIterate(Tag, elementProps, children) {
    const nodeKey = elementProps.key;
    let newProps = elementProps;

    if (Tag === 'a') {
      const currentHost = domain(__SERVER__ ? '' : window.location.href);
      const matchedHost = domain(newProps.href);

      if (matchedHost !== '' && matchedHost !== currentHost) {
        newProps = {
          ...newProps,
          target: '_blank',
          rel: 'nofollow noopener noreferrer',
        };
      }
    }

    if (Tag === 'img') {
      newProps = {
        ...newProps,
        style: { maxWidth: '100%' },
      };
    }

    if (convertLinksToEmbeds) {
      if (
        Tag === 'a' &&
        (elementProps.href.includes('youtube.com/watch') ||
          elementProps.href.includes('youtube.com/shorts') ||
          elementProps.href.includes('youtu.be'))
      ) {
        const youtubeData = getYoutubeVideoData(elementProps.href);
        if (youtubeData) {
          const { embedUrl, maxWidth, aspectRatio } = youtubeData;
          return (
            <iframe
              key={nodeKey}
              width="100%"
              height="auto"
              style={{ aspectRatio: `${aspectRatio} auto`, maxWidth: maxWidth }}
              src={embedUrl}
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
            />
          );
        }
      }

      if (Tag === 'a' && elementProps.href.includes('vimeo.com')) {
        const match = elementProps.href.match(/vimeo\.com\/(\d+)/);

        if (match && match.length > 1 && match[1].length > 0) {
          const videoId = match[1];
          const embedUrl = `https://player.vimeo.com/video/${videoId}`;

          return (
            <iframe
              key={nodeKey}
              width="100%"
              height="auto"
              style={{ aspectRatio: '16/9 auto' }}
              src={embedUrl}
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
            />
          );
        }
      }

      if (
        Tag === 'a' &&
        elementProps.href.includes('soundcloud.com') &&
        !elementProps.href.includes('on.soundcloud.com')
      ) {
        let trackUrl = encodeURIComponent(elementProps.href);

        const embedUrl = `https://w.soundcloud.com/player/?url=${trackUrl}&color=%23ff5500&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true`;

        return (
          <iframe
            key={nodeKey}
            width="100%"
            height="166"
            scrolling="no"
            frameBorder="no"
            allow="autoplay"
            src={embedUrl}
          />
        );
      }

      if (
        Tag === 'a' &&
        elementProps.href.includes('instagram.com') &&
        getInstagramEmbedSrc(elementProps.href)
      ) {
        return <BrowseInstagram key={nodeKey} postUrl={elementProps.href} />;
      }

      if (Tag === 'a' && elementProps.href.includes('tiktok.com')) {
        const tiktokMatch = elementProps.href.match(
          /tiktok\.com\/@[^/]+\/video\/(\d+)/
        );

        if (tiktokMatch && tiktokMatch[1]) {
          const videoId = tiktokMatch[1];
          const embedUrl = `https://www.tiktok.com/player/v1/${videoId}`;

          return (
            <iframe
              key={nodeKey}
              width="100%"
              height="auto"
              style={{
                aspectRatio: '9/16 auto',
                maxWidth: '100%',
                border: 'none',
              }}
              src={embedUrl}
              title="TikTok video player"
              frameBorder="0"
              allow="encrypted-media; fullscreen; picture-in-picture"
            />
          );
        }
      }

      if (Tag === 'a' && elementProps.href.includes('behance.net/gallery')) {
        const postId = elementProps.href
          .split('behance.net/gallery/')[1]
          .split('/')[0];

        const embedUrl = `https://www.behance.net/embed/project/${postId}?ilo0=1`;

        return (
          <iframe
            key={nodeKey}
            src={embedUrl}
            height="auto"
            width="100%"
            style={{ aspectRatio: '1.28/1 auto', maxWidth: '500px' }}
            loading="lazy"
            frameBorder="0"
            allow="clipboard-write; fullscreen"
            referrerPolicy="strict-origin-when-cross-origin"
          />
        );
      }
    }

    const stableChildren =
      children == null
        ? children
        : React.Children.map(children, (child, index) => {
            if (!React.isValidElement(child) || child.key != null) {
              return child;
            }
            return React.cloneElement(child, {
              key: `browse-md-child-${index}`,
            });
          });

    return <Tag {...newProps}>{stableChildren}</Tag>;
  }

  return (
    <MDReactComponent
      markdownOptions={{ linkify: true, typographer: true, breaks: true }}
      onIterate={handleIterate}
      {...rest}
      text={preprocessHashtags(rest.text)}
    />
  );
};

BrowseMarkdown.propTypes = {
  convertLinksToEmbeds: PropTypes.bool,
  suggestedRoles: PropTypes.array,
  suggestedSkills: PropTypes.array,
};

BrowseMarkdown.defaultProps = {
  convertLinksToEmbeds: true,
  suggestedRoles: [],
  suggestedSkills: [],
};

export default BrowseMarkdown;
